import type { IRiskRepository } from "../repositories/RiskRepository";
import type {
  RiskDashboardData,
  RiskListParams,
  RiskListResponse,
  CreateRiskRequest,
  CreateRiskResponse,
  UpdateRiskRequest,
  PatchRiskStatusRequest,
  RiskActivityEntry,
  Risk,
} from "@/pages/IssueRiskTracker/types";

// ─── Interface ───────────────────────────────────────────────────────────────

export interface IRiskUseCase {
  getDashboard(studyId: string): Promise<RiskDashboardData>;
  getRisks(studyId: string, params?: RiskListParams): Promise<RiskListResponse>;
  getRiskById(studyId: string, riskId: string): Promise<Risk>;
  createRisk(
    studyId: string,
    data: CreateRiskRequest,
  ): Promise<CreateRiskResponse>;
  updateRisk(
    studyId: string,
    riskId: string,
    data: UpdateRiskRequest,
  ): Promise<void>;
  patchRiskStatus(
    studyId: string,
    riskId: string,
    data: PatchRiskStatusRequest,
  ): Promise<void>;
  getActivityLog(studyId: string, riskId: string): Promise<RiskActivityEntry[]>;
  deleteRisk(studyId: string, riskId: string): Promise<void>;
}

// ─── Implementation ──────────────────────────────────────────────────────────

export class RiskUseCase implements IRiskUseCase {
  constructor(private readonly riskRepository: IRiskRepository) {}

  getDashboard(studyId: string): Promise<RiskDashboardData> {
    return this.riskRepository.getDashboard(studyId);
  }

  getRisks(
    studyId: string,
    params?: RiskListParams,
  ): Promise<RiskListResponse> {
    return this.riskRepository.getRisks(studyId, params);
  }

  createRisk(
    studyId: string,
    data: CreateRiskRequest,
  ): Promise<CreateRiskResponse> {
    return this.riskRepository.createRisk(studyId, data);
  }

  getRiskById(studyId: string, riskId: string): Promise<Risk> {
    return this.riskRepository.getRiskById(studyId, riskId);
  }

  updateRisk(
    studyId: string,
    riskId: string,
    data: UpdateRiskRequest,
  ): Promise<void> {
    return this.riskRepository.updateRisk(studyId, riskId, data);
  }

  deleteRisk(studyId: string, riskId: string): Promise<void> {
    return this.riskRepository.deleteRisk(studyId, riskId);
  }

  patchRiskStatus(
    studyId: string,
    riskId: string,
    data: PatchRiskStatusRequest,
  ): Promise<void> {
    return this.riskRepository.patchRiskStatus(studyId, riskId, data);
  }

  getActivityLog(
    studyId: string,
    riskId: string,
  ): Promise<RiskActivityEntry[]> {
    return this.riskRepository.getActivityLog(studyId, riskId);
  }
}
